import { provideAppInitializer, inject } from '@angular/core';
import { catchError, firstValueFrom, of } from 'rxjs';
import { Dispatcher } from '@ngrx/signals/events';
import { AuthService } from '../data-access/auth';
import { AUTH_STORAGE_KEYS } from '../constants/auth-storage';
import { authEvents } from '../+state/auth/events';
import { AuthStore } from '../+state/auth/auth.store';
import { ConfigService } from '../services/config';
import { isPublicShareEntry } from '../helpers/public-routes';
let visibilityRefreshAttached = false;

/**
 * Logic to restore the user session from localStorage.
 * Runs during application bootstrap.
 */
async function restoreSession(
  auth: AuthService,
  dispatcher: Dispatcher,
): Promise<void> {
  const refresh = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
  if (!refresh) {
    return;
  }

  if (!auth.needsSessionRefresh()) {
    const access = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
    if (access && auth.isTokenValid(access)) {
      dispatcher.dispatch(authEvents.getUser({ accessToken: access }));
    }
    return;
  }

  try {
    await firstValueFrom(auth.ensureFreshSession());
    const access = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
    if (access && auth.isTokenValid(access)) {
      dispatcher.dispatch(authEvents.getUser({ accessToken: access }));
    }
  } catch {
    auth.clearSession();
  }
}

/** Refresh tokens when the user returns to an idle tab (debounced). */
function setupVisibilityRefresh(auth: AuthService): void {
  if (typeof document === 'undefined' || visibilityRefreshAttached) {
    return;
  }
  visibilityRefreshAttached = true;

  let debounceTimer: ReturnType<typeof setTimeout> | undefined;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      return;
    }
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(() => {
      const refresh = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
      if (!refresh || !auth.needsSessionRefresh()) {
        return;
      }
      auth
        .ensureFreshSession()
        .pipe(catchError(() => of(null)))
        .subscribe();
    }, 300);
  });
}

/**
 * Modern Auth Session Initializer using provideAppInitializer.
 * Replaces the deprecated/verbose APP_INITIALIZER provider object.
 */
export function provideAuthSessionInitializer() {
  return provideAppInitializer(async () => {
    // The public share page never touches a stored session: no refresh, no getUser
    // (and so no notification prompt), no redirect to /login. The tokens stay as they
    // are for the app's next full page load.
    const { features } = inject(ConfigService);
    if (isPublicShareEntry(window.location.pathname, features)) {
      return;
    }

    const auth = inject(AuthService);
    const dispatcher = inject(Dispatcher);

    // We inject AuthStore here to ensure it's instantiated if needed,
    // even if not directly used in the logic.
    inject(AuthStore);

    await restoreSession(auth, dispatcher);
    setupVisibilityRefresh(auth);
  });
}
